import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Menu, X, Globe, ChevronDown } from 'lucide-react';
import { useLang, LANGS } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import LeadModal from './LeadModal';

export default function Navbar() {
  const { t, lang, setLang } = useLang();
  const { user, login } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [leadOpen, setLeadOpen] = useState(false);
  const langRef = useRef(null);
  const location = useLocation();

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  useEffect(() => {
    const close = (e) => { if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false); };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, []);

  const pageSource = location.pathname === '/selection' ? 'selection'
    : location.pathname === '/training' ? 'training'
    : (location.pathname === '/masterclass' || location.pathname === '/3daymc') ? 'masterclass'
    : location.pathname === '/dcapital' ? 'dcapital'
    : 'general';

  const links = [
    { to: '/', label: t('nav.home') },
    { to: '/selection', label: t('nav.selection') },
    { to: '/training', label: t('nav.training') },
  ];

  return (
    <>
      <header className="fixed top-0 inset-x-0 z-50 bg-[#0B1220]/90 backdrop-blur-xl border-b border-slate-800" data-testid="navbar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="nav-logo">
            <span className="font-display text-xl bg-gradient-to-r from-blue-400 to-green-400 bg-clip-text text-transparent whitespace-nowrap">
              ALEFTRADERS
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.to === '/' ? 'home' : l.to.slice(1)}`}
                className={({ isActive }) =>
                  `text-sm font-medium whitespace-nowrap transition-colors ${isActive ? 'text-blue-400' : 'text-slate-300 hover:text-white'}`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3 shrink-0">
            <div className="relative" ref={langRef}>
              <button
                data-testid="language-switcher"
                onClick={() => setLangOpen((v) => !v)}
                className="flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-slate-700 bg-[#1E293B] px-3 py-2 text-xs font-semibold text-slate-300 hover:border-blue-500/60 hover:text-blue-300 transition-colors"
              >
                <Globe className="w-3.5 h-3.5" />
                {lang.toUpperCase()}
                <ChevronDown className="w-3 h-3" />
              </button>
              <AnimatePresence>
                {langOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.18 }}
                    className="absolute right-0 mt-2 w-28 rounded-lg border border-slate-700 bg-[#1E293B] shadow-xl overflow-hidden"
                  >
                    {LANGS.map((l) => (
                      <button
                        key={l.code}
                        data-testid={`lang-option-${l.code}`}
                        onClick={() => { setLang(l.code); setLangOpen(false); }}
                        className={`w-full text-left px-4 py-2.5 text-xs font-semibold transition-colors ${lang === l.code ? 'bg-blue-500/15 text-blue-300' : 'text-slate-400 hover:bg-white/5 hover:text-white'}`}
                      >
                        {l.label}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {user ? (
              <Link
                to="/account"
                data-testid="nav-account-link"
                className="hidden sm:flex items-center gap-2 whitespace-nowrap rounded-lg border border-slate-700 bg-[#1E293B] pl-1.5 pr-3 py-1.5 hover:border-blue-500/60 transition-colors"
              >
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-6 h-6 rounded-full" />
                ) : (
                  <span className="w-6 h-6 rounded-full bg-blue-500/25 flex items-center justify-center text-[10px] font-bold text-blue-300">
                    {(user.name || user.email || '?').slice(0, 1).toUpperCase()}
                  </span>
                )}
                <span className="text-xs font-semibold text-slate-200 max-w-[90px] truncate">{user.name || user.email}</span>
              </Link>
            ) : (
              <button
                data-testid="nav-signin-btn"
                onClick={login}
                className="hidden sm:inline-flex items-center gap-2 whitespace-nowrap rounded-lg border border-slate-600 px-4 py-2.5 text-sm font-semibold text-slate-200 transition-all duration-300 hover:border-blue-500/60 hover:text-blue-300 active:scale-[0.97]"
              >
                {t('nav.signin')}
              </button>
            )}

            <button
              data-testid="nav-apply-btn"
              onClick={() => setLeadOpen(true)}
              className="hidden sm:inline-flex items-center gap-2 whitespace-nowrap rounded-lg bg-blue-500 px-5 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-blue-400 hover:shadow-[0_0_24px_rgba(59,130,246,0.45)] active:scale-[0.97]"
            >
              {t('nav.apply')}
            </button>

            <button
              data-testid="mobile-menu-btn"
              onClick={() => setMobileOpen((v) => !v)}
              className="lg:hidden p-2 text-slate-300"
              aria-label="Menu"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {mobileOpen && (
            <motion.nav
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="lg:hidden overflow-hidden border-t border-slate-800 bg-[#0B1220]"
            >
              <div className="px-6 py-4 space-y-1">
                {links.map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    className={({ isActive }) =>
                      `block py-3 text-sm font-medium ${isActive ? 'text-blue-400' : 'text-slate-300'}`
                    }
                  >
                    {l.label}
                  </NavLink>
                ))}
                <button
                  data-testid="mobile-apply-btn"
                  onClick={() => { setMobileOpen(false); setLeadOpen(true); }}
                  className="mt-2 w-full rounded-lg bg-blue-500 px-5 py-3 text-sm font-semibold text-white"
                >
                  {t('nav.apply')}
                </button>
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </header>
      <LeadModal open={leadOpen} onOpenChange={setLeadOpen} source={pageSource} onSuccess={pageSource === 'training' ? () => { window.location.href = '/training-payment'; } : pageSource === 'selection' ? () => { window.location.href = '/selection-payment'; } : undefined} />
    </>
  );
}
